export interface DragSettings {
  dragSensitivity: number;
}

export interface DragState {
  isPressed: boolean;
  isDragging: boolean;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  startProgress: number;
  lastTime: number;
  velocity: number;
  horizontalVelocity: number;
  verticalMovement: number;
  isClickableTarget: boolean;
}

export interface DragCallbacks {
  onDragStart?: () => void;
  onDragMove?: (progress: number) => void;
  onDragEnd?: (velocity: number) => void;
  onClickableElement?: (element: HTMLElement) => boolean;
}

export class MpsDragController {
  private state: DragState = {
    isPressed: false,
    isDragging: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    startProgress: 0,
    lastTime: 0,
    velocity: 0,
    horizontalVelocity: 0,
    verticalMovement: 0,
    isClickableTarget: false,
  };

  private readonly DRAG_THRESHOLD = 3; // 3px 이상 움직여야 드래그로 인식
  private readonly DELTA_X_THRESHOLD = 10; // 수평 이동 임계값

  constructor(
    private settings: DragSettings,
    private callbacks: DragCallbacks = {},
  ) {}

  // ===========================================
  // 이벤트 핸들러 생성
  // ===========================================

  createStartHandler() {
    return (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      const { x, y } = this.getPointerPosition(e);

      this.state.isPressed = true;
      this.state.isDragging = false;
      this.state.startX = x;
      this.state.startY = y;
      this.state.lastX = x;
      this.state.lastY = y;
      this.state.lastTime = Date.now();
      this.state.velocity = 0;
      this.state.horizontalVelocity = 0;
      this.state.verticalMovement = 0;

      // 클릭 가능한 요소인지 판단
      this.state.isClickableTarget = this.callbacks.onClickableElement?.(target) || false;

      this.callbacks.onDragStart?.();
    };
  }

  createMoveHandler() {
    return (e: MouseEvent | TouchEvent) => {
      if (!this.state.isPressed) return;

      const { x, y } = this.getPointerPosition(e);
      const deltaX = Math.abs(x - this.state.startX);
      const deltaY = Math.abs(y - this.state.startY);
      const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // 드래그 임계값을 넘지 않았으면 아직 클릭일 가능성
      if (!this.state.isDragging && totalMovement < this.DRAG_THRESHOLD) {
        return;
      }
      // 임계값 보다 적은 거리만큼 드래그 된다면 수평 드래그가 아닌 것으로 판단
      if (deltaX < this.DELTA_X_THRESHOLD) {
        return;
      }

      // 처음으로 드래그 임계값을 넘었을 때
      if (!this.state.isDragging) {
        this.state.isDragging = true;

        // 클릭 가능한 요소라면 이벤트 차단
        if (this.state.isClickableTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }

      // 드래그가 시작된 후에는 항상 이벤트 차단
      e.preventDefault();
      e.stopPropagation();

      const currentTime = Date.now();
      this.updateVelocity(x, y, currentTime);

      const progress = this.calculateProgress(x);
      this.callbacks.onDragMove?.(progress);
    };
  }

  createEndHandler() {
    return (e: MouseEvent | TouchEvent) => {
      if (!this.state.isPressed) return;

      this.state.isPressed = false;

      if (this.state.isDragging) {
        // 최종 속도 계산
        const finalVelocity = this.calculateFinalVelocity();
        this.callbacks.onDragEnd?.(finalVelocity);

        // 드래그가 발생했으면 클릭 이벤트 차단
        if (this.state.isClickableTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }

      this.reset();
    };
  }

  // ===========================================
  // 상태 관리
  // ===========================================

  setStartProgress(progress: number): void {
    this.state.startProgress = progress;
  }

  isDragging(): boolean {
    return this.state.isDragging;
  }

  isPressed(): boolean {
    return this.state.isPressed;
  }

  getState(): Readonly<DragState> {
    return this.state;
  }

  reset(): void {
    this.state.isDragging = false;
    this.state.isClickableTarget = false;
    this.state.horizontalVelocity = 0;
    this.state.verticalMovement = 0;
  }

  // ===========================================
  // 설정 업데이트
  // ===========================================

  updateSettings(newSettings: Partial<DragSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
  }

  // ===========================================
  // 내부 메서드
  // ===========================================

  private getPointerPosition(e: MouseEvent | TouchEvent): { x: number; y: number } {
    return 'touches' in e
      ? { x: e.touches[0]?.clientX || 0, y: e.touches[0]?.clientY || 0 }
      : { x: e.clientX, y: e.clientY };
  }

  private updateVelocity(x: number, y: number, currentTime: number): void {
    const deltaX = x - this.state.lastX;
    const deltaY = y - this.state.lastY;
    const deltaTime = currentTime - this.state.lastTime;

    // 수직 이동량 누적
    this.state.verticalMovement += Math.abs(deltaY);

    if (deltaTime > 0) {
      // 전체 이동 거리와 수평 이동 거리 계산
      const totalDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const horizontalRatio = Math.abs(deltaX) / Math.max(totalDistance, 1);

      // 수평 성분이 강할수록 더 높은 가중치 적용
      const horizontalWeight = Math.pow(horizontalRatio, 0.5);

      // dragSensitivity 반영한 속도 계산
      const baseVelocityMultiplier = 15 * this.settings.dragSensitivity;

      // 기존 속도 (전체 이동 기준)
      this.state.velocity = (deltaX * baseVelocityMultiplier) / deltaTime;

      // 수평 속도 (수평 성분만 강조)
      this.state.horizontalVelocity = (deltaX * baseVelocityMultiplier * horizontalWeight) / deltaTime;
    }

    this.state.lastX = x;
    this.state.lastY = y;
    this.state.lastTime = currentTime;
  }

  private calculateProgress(currentX: number): number {
    const totalDeltaX = currentX - this.state.startX;
    const containerWidth = document.documentElement.clientWidth; // 임시로 사용, 실제로는 주입받아야 함
    const totalProgressDelta = -(totalDeltaX / containerWidth) * this.settings.dragSensitivity;
    return this.state.startProgress + totalProgressDelta;
  }

  private calculateFinalVelocity(): number {
    // 수직 이동이 많은 경우 수평 속도 사용
    const useHorizontalVelocity = this.state.verticalMovement > 20;
    const finalVelocity = useHorizontalVelocity ? this.state.horizontalVelocity : this.state.velocity;
    // 최소 속도 보장 로직은 외부에서 처리하도록 원본 속도만 반환
    return finalVelocity;
  }
}
